import * as Apollo from "@apollo/client";
import React from "react";

import * as Context from "~/Context";
import { useContext } from "~/Context";

import * as ELDModule from "./ELD";
import * as RevisionModule from "./Revision";
import * as ServiceErrorModule from "./ServiceError";

export namespace WebAPI {
  export import ELD = ELDModule;
  export import Revision = RevisionModule;
  export import ServiceError = ServiceErrorModule;

  const ClientContext = React.createContext<
    Apollo.ApolloClient<unknown> | undefined
  >(undefined);

  const clients = new Map<string, Apollo.ApolloClient<unknown>>();

  /**
   * The client for a given `Context`, created once per API/viewer pair.
   *
   * Redux effects receive `Context` but cannot call hooks, so they cannot reach
   * the client through `useClient`. Keying the cache on the values the client is
   * built from means an effect and the React tree share one client — and so one
   * `InMemoryCache` — rather than each holding its own.
   */
  export const clientFor = (
    context: Context.Context
  ): Apollo.ApolloClient<unknown> => {
    // Escaped rather than a literal NUL: a NUL cannot appear in either a URL
    // or a JWT, so it is a delimiter no value can forge, but written as a raw
    // byte it makes git treat this file as binary and stop diffing it.
    const key = `${context.api.baseURL}\u0000${context.viewer.token}`;
    const existing = clients.get(key);
    if (existing) return existing;

    // Tokens rotate, so a key that misses means every earlier client is built on
    // a stale one. Drop them rather than accumulating a client per rotation.
    // eslint-disable-next-line functional/no-expression-statement
    clients.clear();

    const client = new Apollo.ApolloClient({
      cache: new Apollo.InMemoryCache(),
      // Points at elogs-mobile-api for now — there's no dedicated elogs
      // web API yet. Swap this URL out once one exists.
      uri: `${context.api.baseURL}/elogs-mobile/graphql`,
      headers: { Authorization: `Bearer ${context.viewer.token}` },
      connectToDevTools: true,
    });

    // eslint-disable-next-line functional/no-expression-statement
    clients.set(key, client);
    return client;
  };

  export const Provider = ({
    children,
  }: {
    readonly children?: React.ReactNode;
  }) => {
    const context = useContext();

    // Not memoised here. `clientFor` is already memoised on exactly the values a
    // client is built from, and a second cache keyed on anything else — such as
    // mount, which is what an empty dependency array means — is a way for the
    // tree to hold a client the effects have already replaced.
    const client = clientFor(context);

    return (
      <ClientContext.Provider value={client}>{children}</ClientContext.Provider>
    );
  };

  export const useClient = (): Apollo.ApolloClient<unknown> => {
    const client = React.useContext(ClientContext);
    if (!client) {
      throw new Error(
        "`WebAPI.useClient` was called outside of a `WebAPI.Provider`."
      );
    }
    return client;
  };
}
